import React, { useState, useEffect } from 'react';
import { Search, Eye, Download, Upload, FileText, Trash2, CheckCircle, XCircle, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api, { studentsAPI, staffAPI, consultantsAPI, masterSettingsAPI } from '@/api/api';
import { useNavigate } from 'react-router-dom';

const Marksheet = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/transcripts/marksheet');
      setDocuments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching marksheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (docId) => {
    try {
      const response = await apiClient.get(`/api/transcripts/documents/${docId}`);
      setSelectedDoc(response.data);
      setShowViewModal(true);
    } catch (error) {
      alert('Failed to load document');
    }
  };

  const handleVerify = async (docId, status) => {
    try {
      await apiClient.put(`/api/transcripts/documents/${docId}/verify?status=${status}`);
      alert(`Marksheet ${status.toLowerCase()} successfully!`);
      fetchDocuments();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this marksheet?')) return;
    try {
      await apiClient.delete(`/api/transcripts/documents/${docId}`);
      fetchDocuments();
    } catch (error) {
      alert('Failed to delete marksheet');
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.roll_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.session?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marksheets</h1>
          <p className="text-sm text-gray-500 mt-1">View and manage student marksheet records</p>
        </div>
        <Button onClick={() => navigate('/transcripts/upload-marksheet')} className="flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Upload Marksheet
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder="Search by student name, roll no, session..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No.</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Semester</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                      <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      No marksheets found. Click "Upload Marksheet" to add a new record.
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium">{doc.entity_name}</td>
                      <td className="px-4 py-3 text-sm">{doc.roll_no || '-'}</td>
                      <td className="px-4 py-3 text-sm">{doc.course || '-'}</td>
                      <td className="px-4 py-3 text-sm">{doc.semester || '-'}</td>
                      <td className="px-4 py-3 text-sm">{doc.session || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          doc.status === 'Verified' ? 'bg-green-100 text-green-800' :
                          doc.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {doc.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleView(doc.id)} className="text-blue-600 hover:text-blue-800" title="View">
                            <Eye className="h-4 w-4" />
                          </button>
                          {doc.status === 'Pending' && (
                            <>
                              <button onClick={() => handleVerify(doc.id, 'Verified')} className="text-green-600 hover:text-green-800" title="Verify">
                                <CheckCircle className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleVerify(doc.id, 'Rejected')} className="text-orange-600 hover:text-orange-800" title="Reject">
                                <XCircle className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button onClick={() => handleDelete(doc.id)} className="text-red-600 hover:text-red-800" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View Modal */}
      {showViewModal && selectedDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Marksheet Details</h2>
              <button onClick={() => setShowViewModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-gray-500">Student Name</Label><p className="font-medium">{selectedDoc.entity_name}</p></div>
                <div><Label className="text-gray-500">Roll No</Label><p className="font-medium">{selectedDoc.roll_no || '-'}</p></div>
                <div><Label className="text-gray-500">Course</Label><p className="font-medium">{selectedDoc.course || '-'}</p></div>
                <div><Label className="text-gray-500">Semester</Label><p className="font-medium">{selectedDoc.semester || '-'}</p></div>
                <div><Label className="text-gray-500">Session</Label><p className="font-medium">{selectedDoc.session || '-'}</p></div>
                <div><Label className="text-gray-500">Status</Label><p className="font-medium">{selectedDoc.status}</p></div>
              </div>
              {selectedDoc.file_data && (
                <div className="mt-4">
                  <Label className="text-gray-500">Document Preview</Label>
                  {selectedDoc.file_type === 'pdf' ? (
                    <iframe src={selectedDoc.file_data} className="w-full h-96 border rounded mt-2" title="Marksheet" />
                  ) : (
                    <img src={selectedDoc.file_data} alt="Marksheet" className="max-w-full h-auto border rounded mt-2" />
                  )}
                </div>
              )}
              <div className="flex gap-2 pt-4">
                {selectedDoc.file_data && (
                  <a href={selectedDoc.file_data} download={selectedDoc.file_name} className="flex-1">
                    <Button variant="outline" className="w-full"><Download className="h-4 w-4 mr-2" />Download</Button>
                  </a>
                )}
                <Button onClick={() => setShowViewModal(false)} className="flex-1">Close</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marksheet;
